import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import VmsPageCard from "../components/VmsPageCard";
import { vmsApi } from "../services/vmsApi";
import { isVmsGeneralAdmin } from "../utils/auth";
import { formatSignedOutBy, getVmsStatusBadge, isPassNotValidForEntry } from "../utils/vmsStatus";

const VmsRequests = () => {
  const isAdmin = isVmsGeneralAdmin();
  const [offices, setOffices] = useState([]);
  const [reroutePersons, setReroutePersons] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({
    office_id: "",
    status: "",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 20,
  });
  const [listMeta, setListMeta] = useState({
    total_count: 0,
    total_pages: 0,
    page: 1,
    page_size: 20,
  });
  const filtersRef = useRef(filters);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [decision, setDecision] = useState({
    action: "approve",
    note: "",
    rescheduled_visit_time: "",
    approved_visit_time: "",
    send_visitor_email: true,
    target_office_id: "",
    target_person_id: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDecisionLoading, setIsDecisionLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportScope, setExportScope] = useState("all");
  const [exportStatus, setExportStatus] = useState("REQUESTED");
  const decisionDisclosure = useDisclosure();
  const filtersDrawer = useDisclosure();
  const drawerDisclosure = useDisclosure();
  const { isOpen: isDecisionOpen, onOpen: onDecisionOpen, onClose: onDecisionClose } = decisionDisclosure;
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = drawerDisclosure;
  const toast = useToast();

  const getRequestVisitorDisplayName = (item) =>
    item?.visitor?.full_name ||
    item?.visitor?.name ||
    item?.visitor_name ||
    item?.name ||
    // Some list endpoints may return the guest under `person_to_meet`
    item?.person_to_meet?.full_name ||
    item?.person_to_meet?.name ||
    "-";

  const prettyDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const getMeetingDisplayName = (item) => {
    const full = item?.person_to_meet?.full_name || item?.person_to_meet?.name || "-";
    const title = item?.person_to_meet?.title;
    return title ? `${full} (${title})` : full;
  };

  const getPurpose = (item) =>
    item?.visitor?.visit_purpose ||
    item?.visitor?.purpose ||
    item?.person_to_meet?.visit_purpose ||
    item?.person_to_meet?.purpose ||
    "-";

  const getRequestedTime = (item) =>
    item?.timestamps?.requested_visit_time || item?.timestamps?.created_at || "-";
  const getScheduledMeetingTime = (item) => item?.card?.scheduled_meeting_time || "-";
  const getOfficeMeetingTime = (item) => item?.timestamps?.office_meeting_time || "-";

  const getGateName = (item) => item?.gate_name || "-";
  const getCurrentOfficeId = (item) => item?.office?.office_id || item?.office_id || "";

  const isPassGenerated = (item) => Boolean(item?.card?.pass_generated);
  const getPassValidForGateLabel = (item) => {
    if (isPassNotValidForEntry(item?.status)) return "No";
    if (!isPassGenerated(item)) return "—";
    return "Yes";
  };
  const isActionableStatus = (item) => (item?.status || "").toUpperCase() === "REQUESTED";
  const getProcessedByName = (item) => item?.processed_by || item?.approved_by_email || item?.declined_by_email || "-";
  const getRerouteAudit = (item) => {
    const reroute = item?.reroute || {};
    return {
      fromOffice:
        reroute?.from_office_name || item?.audit?.from_office_name || item?.rerouted_from_office_name || "-",
      toOffice: reroute?.to_office_name || item?.audit?.to_office_name || item?.rerouted_to_office_name || "-",
      fromPerson:
        reroute?.from_person_name || item?.audit?.from_person_name || item?.rerouted_from_person_name || "-",
      toPerson: reroute?.to_person_name || item?.audit?.to_person_name || item?.rerouted_to_person_name || "-",
    };
  };

  const loadOffices = async () => {
    const data = await vmsApi.getOffices();
    setOffices(Array.isArray(data) ? data : data?.data || []);
  };

  const toIsoIfProvided = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
  };

  const buildRequestParams = (f) => {
    const page = Math.max(1, Number(f.page) || 1);
    const page_size = Math.min(200, Math.max(1, Number(f.page_size) || 20));
    const params = { page, page_size };
    if (f.office_id) params.office_id = f.office_id;
    if (f.status) params.status = f.status;
    const sd = toIsoIfProvided(f.start_date);
    const ed = toIsoIfProvided(f.end_date);
    if (sd) params.start_date = sd;
    if (ed) params.end_date = ed;
    return params;
  };

  const loadRequests = async (nextFilters) => {
    const source = nextFilters ?? filtersRef.current;
    setIsLoading(true);
    try {
      const params = buildRequestParams(source);
      const data = await vmsApi.getRequests(params);
      const list = Array.isArray(data) ? data : data?.requests ?? data?.data ?? [];
      setRequests(Array.isArray(list) ? list : []);
      const isEnvelope = data && typeof data === "object" && !Array.isArray(data);
      const hasPagedShape =
        isEnvelope &&
        (Array.isArray(data.requests) ||
          data.total_count !== undefined ||
          data.total_pages !== undefined ||
          data.page !== undefined);
      if (hasPagedShape) {
        const page_size = data.page_size !== undefined ? Number(data.page_size) : params.page_size;
        const total_count = data.total_count !== undefined ? Number(data.total_count) : list.length;
        const total_pages =
          data.total_pages !== undefined
            ? Number(data.total_pages)
            : total_count === 0
              ? 0
              : Math.max(1, Math.ceil(total_count / page_size));
        setListMeta({
          total_count,
          total_pages,
          page: data.page !== undefined ? Number(data.page) : params.page,
          page_size,
        });
      } else {
        const len = Array.isArray(list) ? list.length : 0;
        setListMeta({
          total_count: len,
          total_pages: len ? 1 : 0,
          page: params.page,
          page_size: params.page_size,
        });
      }
      setFilters((prev) => ({
        ...prev,
        ...source,
        page: hasPagedShape && data.page != null ? Number(data.page) : params.page,
        page_size: hasPagedShape && data.page_size != null ? Number(data.page_size) : params.page_size,
      }));
    } catch (err) {
      toast({ title: "Failed to load requests", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const getListAndMeta = (data, fallbackPageSize = 20) => {
    const list = Array.isArray(data) ? data : data?.requests ?? data?.data ?? [];
    const pageSize = Number(data?.page_size ?? fallbackPageSize);
    const totalCount = Number(data?.total_count ?? (Array.isArray(list) ? list.length : 0));
    const totalPages = Number(
      data?.total_pages ?? (pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1)
    );
    return {
      list: Array.isArray(list) ? list : [],
      totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
    };
  };

  const loadAllRequestsForExport = async (baseFilters) => {
    const firstParams = buildRequestParams({ ...baseFilters, page: 1, page_size: 200 });
    const firstData = await vmsApi.getRequests(firstParams);
    const first = getListAndMeta(firstData, firstParams.page_size);
    let allRows = [...first.list];

    for (let p = 2; p <= first.totalPages; p += 1) {
      const pageData = await vmsApi.getRequests({ ...firstParams, page: p });
      const next = getListAndMeta(pageData, firstParams.page_size);
      allRows = allRows.concat(next.list);
    }
    return allRows;
  };

  const toEscapedHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");

  const exportRequestsPdf = async () => {
    setIsExportingPdf(true);
    try {
      const scopedStatus = exportScope === "status" ? exportStatus : "";
      const baseFilters = {
        ...filtersRef.current,
        status: scopedStatus,
      };
      const rows = await loadAllRequestsForExport(baseFilters);
      const title = scopedStatus ? `Visitors Requests (${scopedStatus})` : "Visitors Requests (All Statuses)";
      const generatedAt = new Date().toLocaleString();

      const tableRowsHtml = rows
        .map((item) => {
          const visitor = toEscapedHtml(getRequestVisitorDisplayName(item));
          const meeting = toEscapedHtml(getMeetingDisplayName(item));
          const office = toEscapedHtml(item?.office_name || item?.office?.name || "-");
          const requestedTime = toEscapedHtml(prettyDateTime(getRequestedTime(item)));
          const purpose = toEscapedHtml(getPurpose(item));
          const status = toEscapedHtml((item?.status || "").toUpperCase() || "UNKNOWN");
          const processedBy = toEscapedHtml(getProcessedByName(item));
          return `<tr><td>${visitor}</td><td>${meeting}</td><td>${office}</td><td>${requestedTime}</td><td>${purpose}</td><td>${status}</td><td>${processedBy}</td></tr>`;
        })
        .join("");

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${toEscapedHtml(title)}</title>
    <style>
      body { font-family: Calibri, "Segoe UI", Arial, sans-serif; margin: 24px; color: #111827; }
      h1 { margin: 0 0 6px; font-size: 22px; color: #0B7A20; }
      .meta { margin: 0 0 14px; color: #6B7280; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #D1D5DB; padding: 6px; text-align: left; vertical-align: top; }
      th { background: #F3F4F6; }
      .empty { padding: 14px; border: 1px solid #D1D5DB; color: #6B7280; }
      @media print { body { margin: 12mm; } }
    </style>
  </head>
  <body>
    <h1>${toEscapedHtml(title)}</h1>
    <p class="meta">Generated: ${toEscapedHtml(generatedAt)} | Total rows: ${rows.length}</p>
    ${
      rows.length
        ? `<table><thead><tr><th>Visitor</th><th>Meeting With</th><th>Office</th><th>Requested Time</th><th>Purpose</th><th>Status</th><th>Processed By</th></tr></thead><tbody>${tableRowsHtml}</tbody></table>`
        : `<div class="empty">No request records to export.</div>`
    }
    <script>window.onload = () => { window.print(); };</script>
  </body>
</html>`;

      const w = window.open("", "_blank", "noopener,noreferrer");
      if (!w) {
        throw new Error("Pop-up blocked. Please allow pop-ups to export PDF.");
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (err) {
      toast({ title: "PDF export failed", description: err?.message, status: "error" });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const loadReroutePersons = async (officeId) => {
    if (!officeId) {
      setReroutePersons([]);
      return;
    }
    try {
      const response = await vmsApi.listPersons({ office_id: officeId, status: "active" });
      setReroutePersons(Array.isArray(response?.persons) ? response.persons : []);
    } catch {
      setReroutePersons([]);
    }
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    loadOffices().catch(() => {});
    loadRequests(filtersRef.current).catch(() => {});
  }, []);

  useEffect(() => {
    if (decision.action !== "reroute") {
      setReroutePersons([]);
      return;
    }
    loadReroutePersons(decision.target_office_id);
  }, [decision.action, decision.target_office_id]);

  const openDecisionWithPreset = (item, presetDecision) => {
    const currentOfficeId = getCurrentOfficeId(item);
    setSelectedRequest(item);
    setDecision({
      action: presetDecision,
      note: "",
      rescheduled_visit_time: "",
      approved_visit_time: "",
      send_visitor_email: true,
      target_office_id: presetDecision === "reroute" ? currentOfficeId : "",
      target_person_id: "",
    });
    onDecisionOpen();
  };

  const openDrawerWithRequest = (item) => {
    setSelectedRequest(item);
    onDrawerOpen();
  };
  const openImagePreview = (imageUrl) => {
    if (!imageUrl) return;
    setPreviewImageUrl(imageUrl);
  };

  const submitDecision = async () => {
    if (!selectedRequest) return;
    if (decision.action === "approve" && !decision.approved_visit_time) {
      toast({
        title: "Visit time required",
        description: "Choose the date and time you want to see this visitor before approving.",
        status: "warning",
      });
      return;
    }
    if ((decision.action === "reschedule" || decision.action === "reroute") && !decision.rescheduled_visit_time) {
      toast({
        title: "Reschedule time required",
        description: "Please select a new visit date/time before submitting.",
        status: "warning",
      });
      return;
    }
    if (decision.action === "reroute" && !decision.target_office_id) {
      toast({
        title: "Target office required",
        description: "Please select a target office for reroute.",
        status: "warning",
      });
      return;
    }
    setIsDecisionLoading(true);
    try {
      const payload = {
        action: decision.action,
        ...(decision.note ? { note: decision.note } : {}),
        ...(decision.action === "approve" && decision.approved_visit_time
          ? {
              approved_visit_time: new Date(decision.approved_visit_time).toISOString(),
              send_visitor_email: Boolean(decision.send_visitor_email),
            }
          : {}),
        ...((decision.action === "reschedule" || decision.action === "reroute") && decision.rescheduled_visit_time
          ? { rescheduled_visit_time: new Date(decision.rescheduled_visit_time).toISOString() }
          : {}),
        ...(decision.action === "reroute" ? { target_office_id: decision.target_office_id } : {}),
        ...(decision.action === "reroute" && decision.target_person_id
          ? { target_person_id: decision.target_person_id }
          : {}),
      };
      const res = await vmsApi.decideRequest(selectedRequest.request_code, payload);
      toast({
        title: "Decision submitted",
        description: res?.pass_token ? `Pass token: ${res.pass_token}` : "Request updated.",
        status: "success",
      });
      onDecisionClose();
      onDrawerClose();
      await loadRequests(filtersRef.current);
    } catch (err) {
      toast({ title: "Decision failed", description: err?.message, status: "error" });
    } finally {
      setIsDecisionLoading(false);
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard title="Visitors Requests">
        <HStack mt={1} spacing={3} align="center" flexWrap="wrap">
          {isAdmin ? (
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filters.office_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, office_id: e.target.value }))}
            >
              <option value="">All offices</option>
              {offices.map((office) => {
                const id = office.id || office.office_id;
                return (
                  <option key={id} value={id}>
                    {office.name || id}
                  </option>
                );
              })}
            </Select>
          ) : null}

          <Select
            w={{ base: "100%", md: "170px" }}
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All status</option>
            <option value="REQUESTED">REQUESTED</option>
            <option value="APPROVED">APPROVED</option>
            <option value="DECLINED">DECLINED</option>
            <option value="RESCHEDULED">RESCHEDULED</option>
            <option value="EXPIRED">EXPIRED</option>
          </Select>

          <HStack spacing={2} w={{ base: "100%", md: "auto" }}>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
            />
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
            />
          </HStack>

          <Button variant="outline" onClick={filtersDrawer.onOpen}>
            Filters
          </Button>
          <Select
            w={{ base: "100%", md: "160px" }}
            value={exportScope}
            onChange={(e) => setExportScope(e.target.value)}
          >
            <option value="all">Export: All</option>
            <option value="status">Export: By Status</option>
          </Select>
          {exportScope === "status" ? (
            <Select
              w={{ base: "100%", md: "170px" }}
              value={exportStatus}
              onChange={(e) => setExportStatus(e.target.value)}
            >
              <option value="REQUESTED">REQUESTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="DECLINED">DECLINED</option>
              <option value="RESCHEDULED">RESCHEDULED</option>
              <option value="EXPIRED">EXPIRED</option>
            </Select>
          ) : null}
          <Button variant="outline" onClick={exportRequestsPdf} isLoading={isExportingPdf}>
            Export PDF
          </Button>
          <Button colorScheme="green" onClick={() => loadRequests({ ...filters, page: 1 })} isLoading={isLoading}>
            Apply Filters
          </Button>
        </HStack>

        <Table size="sm" mt={4}>
          <Thead>
            <Tr>
              <Th>Visitor</Th>
              <Th>Meeting With</Th>
              <Th>Office</Th>
              <Th>Time</Th>
              <Th>Purpose</Th>
              <Th>Status</Th>
              <Th>Processed By / Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((item) => (
              <Tr
                key={item.request_code || item.id}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onClick={() => openDrawerWithRequest(item)}
              >
                <Td>
                  <HStack spacing={2}>
                    <Avatar
                      size="sm"
                      src={item?.visitor?.image || undefined}
                      name={getRequestVisitorDisplayName(item)}
                      bg="gray.100"
                      cursor={item?.visitor?.image ? "zoom-in" : "default"}
                      onClick={(e) => {
                        e.stopPropagation();
                        openImagePreview(item?.visitor?.image);
                      }}
                    />
                    <Text fontWeight="800" fontSize="sm">
                      {getRequestVisitorDisplayName(item)}
                    </Text>
                  </HStack>
                </Td>
                <Td>
                  <Text fontWeight="800" fontSize="sm">
                    {getMeetingDisplayName(item)}
                  </Text>
                </Td>
                <Td>{item.office_name || item.office?.name || "-"}</Td>
                <Td>{prettyDateTime(getRequestedTime(item))}</Td>
                <Td>{getPurpose(item)}</Td>
                <Td>
                  {(() => {
                    const { label, colorScheme } = getVmsStatusBadge(item?.status);
                    return <Badge colorScheme={colorScheme}>{label}</Badge>;
                  })()}
                </Td>
                <Td>
                  {isActionableStatus(item) ? (
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        colorScheme="green"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDecisionWithPreset(item, "approve");
                        }}
                        isDisabled={isLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDecisionWithPreset(item, "decline");
                        }}
                        isDisabled={isLoading}
                      >
                        Decline
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDecisionWithPreset(item, "reschedule");
                        }}
                        isDisabled={isLoading}
                      >
                        Reschedule
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDecisionWithPreset(item, "reroute");
                        }}
                        isDisabled={isLoading}
                      >
                        Reroute
                      </Button>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.700" fontWeight="600">
                      {getProcessedByName(item)}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
            {isLoading && !requests.length ? (
              <Tr>
                <Td colSpan={7}>
                  <Text py={6} textAlign="center" color="gray.500">
                    Loading…
                  </Text>
                </Td>
              </Tr>
            ) : null}
            {!isLoading && !requests.length ? (
              <Tr>
                <Td colSpan={7}>
                  <Text py={6} textAlign="center" color="gray.500">
                    {listMeta.total_count === 0
                      ? "No requests match the current filters."
                      : "No rows on this page."}
                  </Text>
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>

        <HStack mt={3} justify="center" spacing={4}>
          <Button
            variant="ghost"
            onClick={() => {
              const next = { ...filters, page: Math.max(1, Number(listMeta.page || 1) - 1) };
              setFilters(next);
              loadRequests(next);
            }}
            isDisabled={Number(listMeta.page || 1) <= 1 || isLoading}
          >
            Previous
          </Button>
          <Text fontSize="sm" color="gray.600">
            {listMeta.page} / {listMeta.total_pages}
          </Text>
          <Button
            variant="ghost"
            onClick={() => {
              const next = {
                ...filters,
                page: Math.min(Number(listMeta.total_pages || 1), Number(listMeta.page || 1) + 1),
              };
              setFilters(next);
              loadRequests(next);
            }}
            isDisabled={Number(listMeta.page || 1) >= Number(listMeta.total_pages || 1) || isLoading}
          >
            Next
          </Button>
        </HStack>
      </VmsPageCard>

      <Drawer isOpen={filtersDrawer.isOpen} placement="right" onClose={filtersDrawer.onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Advanced Filters</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Page Size</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={filters.page_size}
                  onChange={(e) => setFilters((prev) => ({ ...prev, page_size: Number(e.target.value || 20) }))}
                />
              </FormControl>
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <HStack>
              <Button variant="ghost" onClick={filtersDrawer.onClose}>
                Close
              </Button>
              <Button
                colorScheme="green"
                onClick={() => {
                  filtersDrawer.onClose();
                  loadRequests({ ...filters, page: 1 });
                }}
                isLoading={isLoading}
              >
                Apply
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Request Details</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              {!selectedRequest ? (
                <Text color="gray.500">No request selected.</Text>
              ) : (
                <>
                  <Box>
                    <HStack spacing={3} align="flex-start">
                      <Avatar
                        size="md"
                        src={selectedRequest?.visitor?.image || undefined}
                        name={getRequestVisitorDisplayName(selectedRequest)}
                        bg="gray.100"
                        cursor={selectedRequest?.visitor?.image ? "zoom-in" : "default"}
                        onClick={() => openImagePreview(selectedRequest?.visitor?.image)}
                      />
                      <Box>
                        <Text fontSize="lg" fontWeight="800">
                          {getRequestVisitorDisplayName(selectedRequest)}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Visitor
                        </Text>
                      </Box>
                    </HStack>
                  </Box>

                  <Box>
                    <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                      Meeting With & Office
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm">
                        <b>Meeting With:</b> {getMeetingDisplayName(selectedRequest)}
                      </Text>
                      <Text fontSize="sm">
                        <b>Office:</b> {selectedRequest?.office_name || selectedRequest?.office?.name || "-"}
                      </Text>
                      <Text fontSize="sm">
                        <b>Gate:</b> {getGateName(selectedRequest)}
                      </Text>
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                      Visit Info
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm">
                        <b>Requested Time:</b> {prettyDateTime(getRequestedTime(selectedRequest))}
                      </Text>
                      <Text fontSize="sm">
                        <b>Scheduled Meeting Time:</b> {prettyDateTime(getScheduledMeetingTime(selectedRequest))}
                      </Text>
                      <Text fontSize="sm">
                        <b>Office Meeting Time:</b> {prettyDateTime(getOfficeMeetingTime(selectedRequest))}
                      </Text>
                      <Text fontSize="sm">
                        <b>Signed out:</b> {prettyDateTime(selectedRequest?.timestamps?.signed_out_at || "")}
                      </Text>
                      <Text fontSize="sm">
                        <b>Signed out by:</b> {formatSignedOutBy(selectedRequest?.timestamps?.signed_out_by)}
                      </Text>
                      <Text fontSize="sm">
                        <b>Visit auto-closed:</b>{" "}
                        {selectedRequest?.timestamps?.visit_auto_closed === true
                          ? "Yes"
                          : selectedRequest?.timestamps?.visit_auto_closed === false
                            ? "No"
                            : "—"}
                      </Text>
                      <Text fontSize="sm">
                        <b>Processed By:</b> {getProcessedByName(selectedRequest)}
                      </Text>
                      <Text fontSize="sm">
                        <b>Decision Note:</b> {selectedRequest?.decision_note || "-"}
                      </Text>
                      <Text fontSize="sm">
                        <b>Purpose:</b> {getPurpose(selectedRequest)}
                      </Text>
                      <Text fontSize="sm">
                        <b>Status:</b>{" "}
                        {(() => {
                          const { label, colorScheme } = getVmsStatusBadge(selectedRequest?.status);
                          return <Badge colorScheme={colorScheme}>{label}</Badge>;
                        })()}
                      </Text>
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                      Contact & Verification
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm">
                        <b>Phone:</b> {selectedRequest?.visitor?.phone_number || "-"}
                      </Text>
                      <Text fontSize="sm">
                        <b>Email:</b> {selectedRequest?.visitor?.email || "-"}
                      </Text>
                      <Text fontSize="sm">
                        <b>Address:</b> {selectedRequest?.visitor?.address || "-"}
                      </Text>
                      <Text fontSize="sm">
                        <b>ID:</b> {selectedRequest?.visitor?.identification || "-"}
                      </Text>
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                      Access Pass
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm">
                        <b>Valid for gate entry:</b> {getPassValidForGateLabel(selectedRequest)}
                      </Text>
                      <Text fontSize="sm">
                        <b>Pass Generated:</b> {isPassGenerated(selectedRequest) ? "Yes" : "No"}
                      </Text>
                      {isPassGenerated(selectedRequest) ? (
                        <>
                          <Text fontSize="sm">
                            <b>Pass Code:</b> {selectedRequest?.card?.pass_code || "-"}
                          </Text>
                          <Text fontSize="sm">
                            <b>Expires At:</b> {selectedRequest?.card?.expires_at || "-"}
                          </Text>
                        </>
                      ) : null}
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                      Actions
                    </Text>
                    {isActionableStatus(selectedRequest) ? (
                      <HStack spacing={2}>
                        <Button size="sm" colorScheme="green" onClick={() => openDecisionWithPreset(selectedRequest, "approve")}>
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => openDecisionWithPreset(selectedRequest, "decline")}
                        >
                          Decline
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="orange"
                          variant="outline"
                          onClick={() => openDecisionWithPreset(selectedRequest, "reschedule")}
                        >
                          Reschedule
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          variant="outline"
                          onClick={() => openDecisionWithPreset(selectedRequest, "reroute")}
                        >
                          Reroute
                        </Button>
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.600">
                        This request has already been processed by {getProcessedByName(selectedRequest)}.
                      </Text>
                    )}
                  </Box>

                  {selectedRequest?.reroute || selectedRequest?.rerouted_to_office_name ? (
                    <Box>
                      <Text fontWeight="900" fontSize="sm" color="gray.700" mb={2}>
                        Reroute Audit
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        <Text fontSize="sm">
                          <b>From Office:</b> {getRerouteAudit(selectedRequest).fromOffice}
                        </Text>
                        <Text fontSize="sm">
                          <b>To Office:</b> {getRerouteAudit(selectedRequest).toOffice}
                        </Text>
                        <Text fontSize="sm">
                          <b>From Person:</b> {getRerouteAudit(selectedRequest).fromPerson}
                        </Text>
                        <Text fontSize="sm">
                          <b>To Person:</b> {getRerouteAudit(selectedRequest).toPerson}
                        </Text>
                      </VStack>
                    </Box>
                  ) : null}
                </>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={isDecisionOpen} onClose={onDecisionClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Request Decision</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text w="full" fontSize="sm" color="gray.600">
                Visitor:{" "}
                {getRequestVisitorDisplayName(selectedRequest)}
              </Text>
              <FormControl>
                <FormLabel>Action</FormLabel>
                <Select
                  value={decision.action}
                  onChange={(e) =>
                    setDecision((prev) => {
                      const nextAction = e.target.value;
                      if (nextAction === "reroute") {
                        return {
                          ...prev,
                          action: nextAction,
                          target_office_id: prev.target_office_id || getCurrentOfficeId(selectedRequest),
                          approved_visit_time: "",
                        };
                      }
                      return {
                        ...prev,
                        action: nextAction,
                        target_office_id: "",
                        target_person_id: "",
                        approved_visit_time: nextAction === "approve" ? prev.approved_visit_time : "",
                      };
                    })
                  }
                >
                  <option value="approve">approve</option>
                  <option value="decline">decline</option>
                  <option value="reschedule">reschedule</option>
                  <option value="reroute">reroute</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Note</FormLabel>
                <Input
                  value={decision.note}
                  onChange={(e) => setDecision({ ...decision, note: e.target.value })}
                />
              </FormControl>
              {decision.action === "approve" ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Visit date & time</FormLabel>
                    <Input
                      type="datetime-local"
                      value={decision.approved_visit_time}
                      onChange={(e) => setDecision((prev) => ({ ...prev, approved_visit_time: e.target.value }))}
                    />
                    <FormHelperText>When should this visitor arrive? This is the time you are approving for their visit.</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <Checkbox
                      isChecked={decision.send_visitor_email}
                      onChange={(e) => setDecision((prev) => ({ ...prev, send_visitor_email: e.target.checked }))}
                    >
                      Send confirmation email to visitor
                    </Checkbox>
                    {selectedRequest?.visitor?.email ? (
                      <FormHelperText>{selectedRequest.visitor.email}</FormHelperText>
                    ) : (
                      <FormHelperText color="orange.600">No email on file for this visitor.</FormHelperText>
                    )}
                  </FormControl>
                </>
              ) : null}
              {decision.action === "reschedule" ? (
                <FormControl>
                  <FormLabel>Reschedule Date/Time</FormLabel>
                  <Input
                    type="datetime-local"
                    value={decision.rescheduled_visit_time}
                    onChange={(e) => setDecision({ ...decision, rescheduled_visit_time: e.target.value })}
                  />
                </FormControl>
              ) : null}
              {decision.action === "reroute" ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Target Office</FormLabel>
                    <Select
                      value={decision.target_office_id}
                      onChange={(e) =>
                        setDecision((prev) => ({
                          ...prev,
                          target_office_id: e.target.value,
                          target_person_id: "",
                        }))
                      }
                    >
                      <option value="">Select target office</option>
                      {offices.map((office) => {
                        const id = String(office.id || office.office_id);
                        return (
                          <option key={id} value={id}>
                            {office.name || office.code || id}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Target Person (Optional)</FormLabel>
                    <Select
                      value={decision.target_person_id}
                      onChange={(e) => setDecision((prev) => ({ ...prev, target_person_id: e.target.value }))}
                    >
                      <option value="">Auto-assign / no specific person</option>
                      {reroutePersons.map((person) => {
                        const personId = String(person.person_id || person.id || "");
                        return (
                          <option key={personId} value={personId}>
                            {person.full_name || person.name || personId}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Rescheduled Visit Time</FormLabel>
                    <Input
                      type="datetime-local"
                      value={decision.rescheduled_visit_time}
                      onChange={(e) => setDecision({ ...decision, rescheduled_visit_time: e.target.value })}
                    />
                  </FormControl>
                </>
              ) : null}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={onDecisionClose}>
                Cancel
              </Button>
              <Button colorScheme="green" onClick={submitDecision} isLoading={isDecisionLoading}>
                Submit
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={Boolean(previewImageUrl)} onClose={() => setPreviewImageUrl("")} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" />
          <ModalBody p={0}>
            <Image
              src={previewImageUrl}
              alt="Visitor preview"
              w="100%"
              maxH="80vh"
              objectFit="contain"
              borderRadius="12px"
              bg="blackAlpha.700"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default VmsRequests;
